import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
import { useDispatch } from 'react-redux'
import { clearAccessToken, setAccessToken } from 'lib/auth'
import { reduxForm, InjectedFormProps, SubmissionError } from 'redux-form'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// components
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { Button, ErrorMessage } from 'components/UI'
// constants
import { Forms } from 'constants/forms'
// graphql
import { AUTH_SET_NEW_PASSWORD_MUTATION } from 'graphql/auth/mutations'
// redux
import { setCurrentUser } from 'store/reducers/currentUser'
import { setTwoFactorToken } from 'store/reducers/twoFactorAuth'
// views
import { AuthForm, AuthHeader, ButtonWrapper } from 'themes/views/auth'
// types
import { ParsedUrlQuery } from 'querystring'
import { IAuthUserWithCredentialsResponse } from 'types/graphql'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { clearRedirectUrl, getRedirectUrl } from 'utils/cookies'
import { password, passwordsMatch, required } from 'utils/validators'

type FormData = {
  password: string
  confirmPassword: string
}

type MutationResponse = {
  authSetNewPassword: IAuthUserWithCredentialsResponse
}

interface PageQuery extends ParsedUrlQuery {
  email: string
  domain: string
  resetPasswordToken: string
}

const ResetPasswordConfirm: React.FC<InjectedFormProps<FormData>> = ({ valid, error, handleSubmit }) => {
  const router = useRouter()
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const [setNewPasswordMutation] = useMutation<MutationResponse>(AUTH_SET_NEW_PASSWORD_MUTATION)

  const query = router.query as PageQuery

  const handleFormSubmit = async (formData: FormData) => {
    try {
      await client.clearStore()

      clearAccessToken()

      const {
        data: {
          authSetNewPassword: { data }
        }
      } = await setNewPasswordMutation({
        variables: {
          email: decodeURI(query.email),
          token: decodeURI(query.resetPasswordToken),
          domain: decodeURI(query.domain),
          password: formData.password
        }
      })

      // Check if 2FA is required
      if (data.twoFactorRequired && data.twoFactorToken) {
        // Store token in Redux (memory only, expires in 5 minutes)
        dispatch(
          setTwoFactorToken({
            token: data.twoFactorToken,
            expiryMinutes: 5
          })
        )

        // Redirect to 2FA verification page
        await router.push('/login/verify-2fa')
        return
      }

      // Regular flow (no 2FA)
      dispatch(setCurrentUser(data.user))

      setAccessToken(data.accessToken)

      const redirectUrl = getRedirectUrl()

      if (redirectUrl) {
        clearRedirectUrl()

        await router.push(redirectUrl)
      } else {
        await router.push('/home')
      }
    } catch (errors) {
      console.log('[ResetPasswordConfirm.handleFormSubmit]: ', parseGraphQLErrorMessages(errors))
      throw new SubmissionError({ _error: parseGraphQLErrorMessages(errors) })
    }
  }

  React.useEffect(() => {
    const resetAt = router.query.resetAt as string
    const email = router.query.email as string
    const domain = router.query.domain as string

    if (!resetAt) {
      return
    }

    const isExpired = new Date().getTime() - Number(resetAt) > 30 * 60 * 1000

    if (isExpired) {
      router.push({
        pathname: '/reset-password',
        query: { email, domain }
      })
    }
  }, [router.query.resetAt, router.query.email, router.query.domain])

  return (
    <>
      <AuthHeader>CHANGE PASSWORD</AuthHeader>
      <AuthForm onSubmit={handleSubmit(handleFormSubmit)}>
        <ReduxInput type='password' name='password' label='New Password' validate={[required, password]} />
        <ReduxInput
          type='password'
          name='confirmPassword'
          label='Retype New Password'
          validate={[required, passwordsMatch]}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <ButtonWrapper>
          <Button variant='contained' fullWidth type='submit' disabled={!valid}>
            SAVE
          </Button>
        </ButtonWrapper>
      </AuthForm>
    </>
  )
}

export default reduxForm<FormData>({ form: Forms.RESET_PASSWORD_CONFIRM_FORM })(ResetPasswordConfirm)
